import type { Store as ReduxStore } from "@reduxjs/toolkit";

import { ACCOUNT_CONTEXT } from "fond/constants";
import { setCurrentAccountId } from "fond/redux/accountContextSlice";
import { getItem, setItem } from "fond/utils/localStorage";

/**
 * Store reference - initialized after store creation to avoid circular dependency.
 * Uses dependency injection pattern: store.js calls initAccountContextStorage() after creating the store.
 */
let storeRef: ReduxStore | null = null;

/**
 * Initialize account context storage with the Redux store.
 * Must be called from store.js after store creation.
 *
 * @param store - The Redux store instance
 */
export const initAccountContextStorage = (store: ReduxStore) => {
  storeRef = store;
};

const getStore = () => storeRef;

type AccountContext = Record<string, string>;

/**
 * Get the raw account context object from localStorage or an empty object
 *
 * @returns the existing account context from localStorage or an empty object
 */
const getRawAccountContextFromLocalStorage = (): AccountContext => {
  const context = getItem(ACCOUNT_CONTEXT);
  if (!context) {
    return {};
  }
  return context;
};

/**
 * Get the account context for a given user.
 * Reads from Redux state (source of truth), not localStorage.
 *
 * @param _userId - the user ID (unused, kept for backwards compatibility)
 * @returns the current account ID from Redux state
 */
export const getAccountContext = (_userId?: string): string | undefined => {
  const store = getStore();
  if (!store) {
    // During test setup or early initialization, store might not be initialized yet
    return undefined;
  }
  const state = store.getState();
  return state.accountContext?.currentAccountId || undefined;
};

/**
 * Set the account context to a given account ID for a user.
 * Updates both Redux state (source of truth) and localStorage (for cross-tab communication).
 *
 * @param userId - the user ID to set the account context for
 * @param accountId - the account ID to set the context to
 */
export const setAccountContext = (userId: string, accountId: string) => {
  if (!userId) {
    throw new Error("Could not set account context. Missing user ID.");
  }
  if (!accountId) {
    throw new Error("Could not set account context. Missing account ID.");
  }

  // Update Redux state (source of truth)
  const store = getStore();
  if (store) {
    store.dispatch(setCurrentAccountId(accountId));
  }

  // Update localStorage for cross-tab communication
  const context = getRawAccountContextFromLocalStorage();
  setItem(ACCOUNT_CONTEXT, { ...context, [userId]: accountId });
};

/**
 * Initialize the account context from localStorage on app startup.
 * Should be called once when the app loads.
 *
 * @param userId - the user ID to initialize the account context for
 */
export const initializeAccountContext = (userId: string) => {
  const context = getRawAccountContextFromLocalStorage();
  const accountId = context?.[userId];

  const store = getStore();
  if (store) {
    store.dispatch(setCurrentAccountId(accountId));
  }
};
